import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Button,
  TextField,
  Chip,
  Grid,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import CompleteReviewDialog from './components/CompleteReviewDialog';

const PolicyReviewDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [reviewComment, setReviewComment] = useState('');
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);

  // 修改保单数据结构，添加 reviewHistory 数组
  const [policyData, setPolicyData] = useState({
    id: id,
    policyNumber: 'POL-2024-001',
    type: 'Commercial Property Insurance',
    client: 'ABC Manufacturing Co.',
    status: 'Under Review',
    documents: [
      {
        id: 1,
        name: 'Property Valuation Report.pdf',
        type: 'Valuation',
        status: 'pending',
        remarks: '',
        reviewNote: ''
      },
      {
        id: 2,
        name: 'Risk Assessment.pdf',
        type: 'Risk',
        status: 'pending',
        remarks: '',
        reviewNote: ''
      }
    ],
    reviewNotes: [],
    reviewHistory: []  // 初始化 reviewHistory 数组
  });

  // 状态配置
  const STATUS_CONFIG = {
    pending: {
      color: '#0288d1',
      bgColor: '#e3f2fd',
      label: 'Pending Review'
    },
    approved: {
      color: '#2e7d32',
      bgColor: '#e8f5e9',
      label: 'Approved'
    },
    rejected: {
      color: '#d32f2f',
      bgColor: '#ffebee',
      label: 'Rejected'
    }
  };

  // 获取状态颜色
  const getStatusColor = (status) => {
    return STATUS_CONFIG[status]?.color || STATUS_CONFIG.pending.color;
  };

  // 获取状态标签
  const getStatusChip = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return (
      <Chip
        size="small"
        label={config.label}
        sx={{ 
          bgcolor: config.bgColor,
          color: config.color,
          '& .MuiChip-label': { 
            px: 1.5,
            fontWeight: 500
          }
        }}
      />
    );
  };

  // 修改笔记处理函数
  const handleNoteChange = (docId, note) => {
    setPolicyData(prev => ({
      ...prev,
      documents: prev.documents.map(doc => 
        doc.id === docId ? { ...doc, reviewNote: note } : doc
      )
    }));
  };

  // 简化的状态处理
  const handleDocumentReview = (docId, status) => {
    setPolicyData(prev => {
      const doc = prev.documents.find(d => d.id === docId);
      const note = doc.reviewNote.trim();
      
      return {
        ...prev,
        documents: prev.documents.map(d => 
          d.id === docId ? { 
            ...d, 
            status,
            remarks: note,
            reviewNote: '' // 清空输入框
          } : d
        ),
        reviewNotes: [
          {
            note: `Document "${doc.name}" marked as ${status}${note ? `: ${note}` : ''}`,
            timestamp: new Date().toISOString(),
          },
          ...prev.reviewNotes
        ]
      };
    });
  };

  // 修改状态配置
  const documentStatusConfig = {
    Approved: {
      color: '#2e7d32',
      bgColor: '#e8f5e9',
      icon: <CheckCircleIcon fontSize="small" />,
      label: 'Approved'
    },
    'Needs Revision': {
      color: '#ed6c02',
      bgColor: '#fff3e0',
      icon: <WarningIcon fontSize="small" />,
      label: 'Needs Revision'
    },
    Rejected: {
      color: '#d32f2f',
      bgColor: '#ffebee',
      icon: <CancelIcon fontSize="small" />,
      label: 'Rejected'
    },
    Pending: {
      color: '#0288d1',
      bgColor: '#e3f2fd',
      icon: <PendingIcon fontSize="small" />,
      label: 'Pending Review'
    }
  };

  const handleDocumentClick = (doc) => {
    // 在新窗口打开文档预览页面
    const previewUrl = `/underwriter/document-preview/${doc.id}`;
    const documentState = { document: doc };
    
    // 将 state 数据编码并作为 URL 参数传递
    const stateParam = encodeURIComponent(JSON.stringify(documentState));
    const fullUrl = `${previewUrl}?state=${stateParam}`;
    
    // 使用 window.open 在新窗口打开
    window.open(fullUrl, '_blank');
  };

  const handleDownload = (doc) => {
    console.log('Downloading:', doc.name);
  };

  // 修改提交处理函数
  const handleSubmitReview = () => {
    // 即使没有评论也可以提交
    const finalNote = reviewComment.trim() 
      ? reviewComment 
      : 'Review submitted without additional comments';

    // 更新 policyData 中的 reviewNotes
    setPolicyData(prev => ({
      ...prev,
      reviewNotes: [
        {
          note: finalNote,
          timestamp: new Date().toISOString(),
          type: 'final_review'
        },
        ...prev.reviewNotes
      ]
    }));

    // 打开完成审核对话框
    setOpenCompleteDialog(true);
  };

  // 修改状态处理函数，添加文件名信息
  const handleUpdateDocumentStatus = (docId, newStatus, note) => {
    setPolicyData(prev => {
      const updatedDoc = prev.documents.find(doc => doc.id === docId);
      return {
        ...prev,
        documents: prev.documents.map(doc => {
          if (doc.id === docId) {
            return {
              ...doc,
              status: newStatus,
              remarks: note || doc.remarks,
              updatedAt: new Date().toISOString()
            };
          }
          return doc;
        }),
        reviewHistory: [
          {
            note: `Document "${updatedDoc.name}" status updated to ${newStatus}${note ? `\nNote: ${note}` : ''}`,
            date: new Date().toISOString(),
            author: 'Sarah Wilson',
            type: 'document_review'
          },
          ...prev.reviewHistory
        ]
      };
    });
  };

  const handleCompleteReview = () => {
    // 处理完成审核的逻辑
    console.log('Review completed');
    navigate('/underwriter/approved');
  };

  // 修改状态计算函数
  const getReviewProgress = () => {
    const totalDocs = policyData.documents.length;
    const reviewedDocs = policyData.documents.filter(doc => 
      doc.status === 'Approved' || doc.status === 'Rejected' || doc.status === 'Needs Revision'
    ).length;
    return (reviewedDocs / totalDocs) * 100;
  };

  // 修改文档状态显示函数，添加错误处理
  const getDocumentStatusChip = (status) => {
    const config = documentStatusConfig[status] || documentStatusConfig['Pending']; // 添加默认值
    return (
      <Chip
        size="small"
        icon={config.icon}
        label={config.label}
        sx={{ 
          bgcolor: config.bgColor,
          color: config.color,
          '& .MuiChip-label': { 
            px: 1.5,
            fontWeight: 500
          },
          '& .MuiChip-icon': {
            color: 'inherit'
          }
        }}
      />
    );
  };

  // 修改删除笔记的处理函数
  const handleDeleteNote = (timestamp) => {
    setPolicyData(prev => {
      // 找到要删除的笔记
      const noteToDelete = prev.reviewNotes.find(note => note.timestamp === timestamp);
      
      // 从笔记内容中提取文档信息和状态
      const match = noteToDelete.note.match(/Document "([^"]+)" marked as (\w+)/);
      if (match) {
        const [, documentName, status] = match;
        
        // 更新文档状态
        const updatedDocuments = prev.documents.map(doc => {
          if (doc.name === documentName) {
            return {
              ...doc,
              status: 'pending',  // 重置为待审核状态
              remarks: '',        // 清空备注
              reviewNote: ''      // 清空审核笔记
            };
          }
          return doc;
        });

        // 返回更新后的状态
        return {
          ...prev,
          documents: updatedDocuments,
          reviewNotes: prev.reviewNotes.filter(note => note.timestamp !== timestamp)
        };
      }

      // 如果不是文档审核的笔记，只删除笔记
      return {
        ...prev,
        reviewNotes: prev.reviewNotes.filter(note => note.timestamp !== timestamp)
      };
    });
  };

  // 获取笔记的状态和颜色
  const getNoteStatusAndColor = (noteText) => {
    if (noteText.includes('approved')) {
      return {
        color: STATUS_CONFIG.approved.color,
        borderColor: STATUS_CONFIG.approved.color,
        bgColor: STATUS_CONFIG.approved.bgColor
      };
    }
    if (noteText.includes('rejected')) {
      return {
        color: STATUS_CONFIG.rejected.color,
        borderColor: STATUS_CONFIG.rejected.color,
        bgColor: STATUS_CONFIG.rejected.bgColor
      };
    }
    return {
      color: STATUS_CONFIG.pending.color,
      borderColor: STATUS_CONFIG.pending.color,
      bgColor: STATUS_CONFIG.pending.bgColor
    };
  };

  // 修改文档卡片中的审核操作区域
  const DocumentReviewActions = ({ doc }) => {
    const [showRejectOptions, setShowRejectOptions] = useState(false);

    return (
      <Box sx={{ 
        p: 2, 
        bgcolor: '#f8f9fa',
        borderRadius: 1,
        border: '1px solid #e0e0e0'
      }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#1976d2' }}>
          Review Decision
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Add review notes..."
          value={doc.newNote || ''}
          onChange={(e) => {
            setPolicyData(prev => ({
              ...prev,
              documents: prev.documents.map(d => 
                d.id === doc.id ? { ...d, newNote: e.target.value } : d
              )
            }));
          }}
          sx={{ mb: 2 }}
        />
        
        {/* 审核操作按钮组 */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          {showRejectOptions ? (
            <>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => {
                  handleUpdateDocumentStatus(doc.id, 'Needs Revision', doc.newNote);
                  setShowRejectOptions(false);
                }}
                startIcon={<WarningIcon />}
              >
                Request Revision
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => {
                  handleUpdateDocumentStatus(doc.id, 'Rejected', doc.newNote);
                  setShowRejectOptions(false);
                }}
                startIcon={<CancelIcon />}
              >
                Reject
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowRejectOptions(false)}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => setShowRejectOptions(true)}
                startIcon={<ErrorIcon />}
              >
                Decline
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => handleUpdateDocumentStatus(doc.id, 'Approved', doc.newNote)}
                startIcon={<CheckCircleIcon />}
              >
                Approve
              </Button>
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Container maxWidth="xl">
      {/* Header Section */}
      <Box sx={{ 
        pt: 4,
        pb: 3,
        textAlign: 'center',
        position: 'relative',
        borderBottom: '1px solid #e0e0e0',
        mb: 4
      }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/underwriter/approved')}
          startIcon={<ArrowBackIcon />}
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Back to Review List
        </Button>

        <Typography 
          variant="h4" 
          sx={{ 
            color: '#1976d2',
            fontWeight: 600,
            mb: 1
          }}
        >
          Policy Review Details
        </Typography>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            color: '#666',
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Review policy details and make underwriting decisions
        </Typography>
      </Box>

      {/* Main Content - Two Column Layout */}
      <Grid container spacing={3}>
        {/* Left Column - Documents Section */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" color="primary">
                Document Review
              </Typography>
              <Chip 
                label={`${policyData.documents.length} Documents`}
                color="primary"
                sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
              />
            </Box>

            {/* 文档列表 */}
            {policyData.documents.map((doc) => (
              <Paper
                key={doc.id}
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  border: '1px solid #e0e0e0',
                  borderLeft: `4px solid ${getStatusColor(doc.status)}`,
                  bgcolor: doc.status !== 'pending' ? '#fafafa' : '#fff', // 已审核的背景色略微变暗
                  position: 'relative',
                  transition: 'all 0.3s ease'
                }}
              >
                {/* 文档头部信息 */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start', 
                  mb: 2
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 500, 
                        mb: 0.5,
                        color: doc.status !== 'pending' ? '#666' : '#333'
                      }}
                    >
                      {doc.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Type: {doc.type}
                      </Typography>
                      {getStatusChip(doc.status)}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title="Preview">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDocumentClick(doc)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* 审核操作区域 */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2, 
                  alignItems: 'flex-start', 
                  mt: 2
                }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    placeholder="Add review note (optional)"
                    value={doc.reviewNote}
                    onChange={(e) => handleNoteChange(doc.id, e.target.value)}
                    sx={{ 
                      flex: 1,
                      bgcolor: doc.status !== 'pending' ? '#f5f5f5' : '#fff'
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant={doc.status === 'rejected' ? 'contained' : 'outlined'}
                      color="error"
                      onClick={() => handleDocumentReview(doc.id, 'rejected')}
                      startIcon={<ErrorIcon />}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Reject
                    </Button>
                    <Button
                      variant={doc.status === 'approved' ? 'contained' : 'outlined'}
                      color="success"
                      onClick={() => handleDocumentReview(doc.id, 'approved')}
                      startIcon={<CheckCircleIcon />}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Approve
                    </Button>
                  </Box>
                </Box>
                
                {/* 显示已保存的备注 */}
                {doc.remarks && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      mt: 2,
                      pl: 2,
                      py: 1,
                      borderLeft: `2px solid ${getStatusColor(doc.status)}`,
                      bgcolor: 'rgba(0, 0, 0, 0.02)',
                      borderRadius: '0 4px 4px 0'
                    }}
                  >
                    Note: {doc.remarks}
                  </Typography>
                )}
              </Paper>
            ))}
          </Paper>
        </Grid>

        {/* Right Column - Review Notes */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ p: 3, height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon /> Review Notes
              </Typography>
              <Chip 
                label={`${policyData.reviewNotes.length} Notes`}
                size="small"
                sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
              />
            </Box>

            {/* 优化的 Notes Timeline */}
            <Box sx={{ flex: 1, overflow: 'auto', mb: 3 }}>
              {policyData.reviewNotes.map((note, index) => {
                const { color, borderColor, bgColor } = getNoteStatusAndColor(note.note.toLowerCase());
                
                return (
                  <Box
                    key={index}
                    sx={{
                      mb: 2,
                      p: 2,
                      bgcolor: bgColor,
                      borderRadius: 1,
                      border: '1px solid #e0e0e0',
                      borderLeft: `4px solid ${borderColor}`,
                      position: 'relative',
                      '&:hover .delete-button': {
                        opacity: 1
                      }
                    }}
                  >
                    {/* 时间戳和删除按钮行 */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1
                    }}>
                      <Typography 
                        variant="caption" 
                        sx={{ color: `${color}` }}
                      >
                        {new Date(note.timestamp).toLocaleString()}
                      </Typography>
                      <IconButton
                        size="small"
                        className="delete-button"
                        onClick={() => handleDeleteNote(note.timestamp)}
                        sx={{ 
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          p: 0.5,
                          color: '#666',
                          '&:hover': {
                            color: color
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* 笔记内容 */}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#333',
                        lineHeight: 1.5
                      }}
                    >
                      {note.note}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* 修改最终提交区域 */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e0e0e0'
              }}
            >
              <Typography variant="subtitle1" sx={{ color: '#1976d2', fontWeight: 500, mb: 2 }}>
                Submit Review
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Add additional comments (optional)..."
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                sx={{ mb: 2 }}
              />
              
              <Button
                fullWidth
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleSubmitReview}
                sx={{
                  bgcolor: '#1976d2',
                  '&:hover': {
                    bgcolor: '#1565c0'
                  }
                }}
              >
                Submit Review
              </Button>
            </Paper>
          </Paper>
        </Grid>
      </Grid>

      {/* 完成审核对话框 */}
      <CompleteReviewDialog
        open={openCompleteDialog}
        onClose={() => {
          setOpenCompleteDialog(false);
          setReviewComment(''); // 关闭对话框时清空评论
        }}
        onSubmit={handleCompleteReview}
        documents={policyData.documents.map(doc => ({
          ...doc,
          type: doc.type,
          reviewDate: new Date().toLocaleDateString()
        }))}
        reviewComment={reviewComment} // 传递当前的 reviewComment
      />
    </Container>
  );
};

export default PolicyReviewDetailPage; 